import { LayersList } from '@deck.gl/core';
import Box from '@mui/material/Box';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Circle, GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { isNull } from 'lodash-es';
import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createRoot, Root } from 'react-dom/client';

import { getMapStyles } from '~components/Maps/mapStyles';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';

import { MapLayersToggle } from '../Controls/MapLayersToggle';
import { LngLat } from '../interfaces';
import {
  GeofenceType,
  GOOGLE_MAPS_LOADER_OPTIONS,
  MAP_RECENTER_SPEED_MS,
  MarkerType,
  MAX_ZOOM_CAP_LIFT_FALLBACK_MS,
} from './constants';
import { EditableCircle } from './EditableCircle';
import {
  buildCircles,
  buildPolygons,
  filterFiniteMarkers,
  isFiniteLngLat,
} from './mapV2Geometry';
import { TreadMarker } from './TreadMarker';
import { TreadMarkerProps } from './types';
import useDeckOverlay from './useDeckOverlay';

type MapTypeId = 'hybrid' | 'satellite' | 'terrain' | 'roadmap';

export interface MapControlProps {
  map?: google.maps.Map;
}

type MapV2Props = {
  center?: LngLat;
  drawingMode?: boolean;
  drawingCoordinates?: LngLat[];
  emptyText?: string;
  markers?: TreadMarkerProps[];
  maxZoom?: number;
  // Callback to publish the new coordinates as a polygon is edited
  onPolygonEdit?: (coordinates: LngLat[]) => void;
  onClick?: (position: LngLat) => void;
  overlayLayers?: LayersList;
  allowOverrideUserBounds?: boolean;
  // Position to recenter the map to if it goes out of view
  recenterPosition?: LngLat | null;
  // When true, always pan to recenterPosition when it changes (ignores bounds check)
  forceRecenter?: boolean;
  children?: ReactElement<MapControlProps> | ReactElement<MapControlProps>[];
  // Callback when bounds have been reset (allows parent to clear reset flags)
  onBoundsReset?: () => void;
  // When true, skip fitBounds when entering drawing mode (useful when recenter handles positioning)
  skipDrawingModeFitBounds?: boolean;
  /** Editable circle (e.g. Job Geo Debug): center in meters, radius in meters. When set with onEditableCircleChange, circle is draggable and radius is editable. */
  editableCircleCenter?: LngLat | null;
  editableCircleRadius?: number | null;
  onEditableCircleChange?: (center: LngLat, radius: number) => void;
  /** When true, the layers toggle control is not shown (e.g. Job Geo Debug). */
  hideLayersToggle?: boolean;
  /** Read-only polygon (e.g. other site geofence on Job Geo Debug). */
  readOnlyGeofencePolygon?: LngLat[] | null;
  /** Read-only circle (e.g. other site geofence on Job Geo Debug). */
  readOnlyGeofenceCircle?: { center: LngLat; radius: number } | null;
  /** When false, do not hide the primary circle marker (lat-lon-circle-marker) when an editable circle is shown. Use when the editable circle is an additional geofence, not the primary. Default true. */
  editableCircleReplacesPrimaryMarker?: boolean;
  /** When set, circle with this marker id is orange (selected), all other circles and polygons are blue. Used by site form for selected-geofence styling. */
  selectedCircleMarkerId?: string | null;
};

// Unmounting the layers-toggle root must be deferred one tick: both call sites
// (onMapLoad/onMapUnmount) run inside the main tree's commit phase — GoogleMap
// lifecycle callbacks — where React rejects a synchronous unmount of another
// root with a mid-render warning (TRE-15924).
function unmountMapLayersToggleRoot(rootRef: { current: Root | null }) {
  const root = rootRef.current;
  if (!root) {
    return;
  }

  rootRef.current = null;
  setTimeout(() => root.unmount(), 0);
}

export function MapV2({
  center,
  drawingMode,
  drawingCoordinates = [],
  emptyText,
  markers: markersProp = [],
  // For reference, 17 is well suited for site creation, while 14 is more in line with live map
  maxZoom = 17,
  onPolygonEdit,
  onClick,
  overlayLayers,
  recenterPosition,
  forceRecenter = false,
  children,
  allowOverrideUserBounds = true,
  onBoundsReset,
  skipDrawingModeFitBounds = false,
  editableCircleCenter = null,
  editableCircleRadius = null,
  onEditableCircleChange,
  hideLayersToggle = false,
  readOnlyGeofencePolygon = null,
  readOnlyGeofenceCircle = null,
  editableCircleReplacesPrimaryMarker = true,
  selectedCircleMarkerId = null,
}: MapV2Props) {
  const theme = useTheme();
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [mapTypeId, setMapTypeId] = useState<MapTypeId>('roadmap');
  const drawableRef = useRef<google.maps.Polygon | null>(null);
  const listenersRef = useRef([]);
  const mapLayersToggleRootRef = useRef<Root | null>(null);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const recenterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxZoomCapListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const maxZoomCapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if initial bounds have been set to prevent re-fitting after user interaction
  const initialBoundsSetRef = useRef(false);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const { isWebGLAvailable } = useDeckOverlay({
    layers: overlayLayers,
    map: mapInstance,
  });

  // Add a ref to track programmatic changes
  const isProgrammaticMapEvent = useRef(true);

  // Drop any marker with a non-finite coordinate before it reaches Google Maps.
  // setCenter/fitBounds throw InvalidValueError on NaN and corrupt the map
  // projection, which then crashes the deck.gl overlay (Sentry HORIZON-FE-2XP).
  // Callers like the order-form site picker can emit NaN coords for a site that
  // has no lat/lng.
  const markers = useMemo(() => filterFiniteMarkers(markersProp), [markersProp]);

  // Add handlers for user interaction - once user interacts, never auto-fit bounds again
  const handleUserInteraction = useCallback(() => {
    if (!isProgrammaticMapEvent.current) {
      setUserHasInteracted(true);
      // Once user has interacted, mark initial bounds as set to prevent future auto-fits
      initialBoundsSetRef.current = true;
    }
  }, []);

  const clearPendingMaxZoomCapLift = useCallback(() => {
    maxZoomCapListenerRef.current?.remove();
    maxZoomCapListenerRef.current = null;
    clearTimeout(maxZoomCapTimeoutRef.current ?? undefined);
    maxZoomCapTimeoutRef.current = null;
  }, []);

  const fitBounds = useCallback(
    (bounds: google.maps.LatLngBounds) => {
      if (mapInstance) {
        isProgrammaticMapEvent.current = true;
        // A zero-area single-marker bounds fits to Google's max zoom (~z21).
        // Google honors the map's maxZoom option while computing the fit, so cap
        // it for the fit and lift the cap once the map settles, leaving the user
        // free to zoom in further afterwards. A maxZoom-only setOptions doesn't
        // touch zoomControl, so it avoids the control-rebuild click-suppression
        // hazard documented on the mapOptions memo below.
        mapInstance.setOptions({ maxZoom });
        mapInstance.fitBounds(bounds);
        // A superseded fit's pending lift must not fire against a later fit or
        // a map this component no longer owns (same discipline as
        // useDeckOverlay's idle-listener teardown).
        clearPendingMaxZoomCapLift();
        const liftMaxZoomCap = () => {
          clearPendingMaxZoomCapLift();
          mapInstance.setOptions({ maxZoom: null });
          // Google's zoom control only recomputes the +/- buttons' disabled
          // state on zoom events, not on a maxZoom option change, so after a
          // fit that landed exactly on the cap the + button stays disabled
          // even though the map can zoom again. Nudge the control to re-read.
          window.google.maps.event.trigger(mapInstance, 'zoom_changed');
        };
        maxZoomCapListenerRef.current = window.google.maps.event.addListenerOnce(
          mapInstance,
          'idle',
          liftMaxZoomCap,
        );
        maxZoomCapTimeoutRef.current = setTimeout(
          liftMaxZoomCap,
          MAX_ZOOM_CAP_LIFT_FALLBACK_MS,
        );
        // Reset the isProgrammaticChange flag after a short delay to ensure all events have fired
        setTimeout(() => {
          isProgrammaticMapEvent.current = false;
        }, 100);
      }
    },
    [mapInstance, maxZoom, clearPendingMaxZoomCapLift],
  );

  // Changing to drawing mode triggers coordinate hydration and map fit
  useEffect(() => {
    if (mapInstance && drawingMode) {
      // Skip fitBounds entirely if the prop is set (e.g., for Geofence Reports where recenter handles positioning)
      if (skipDrawingModeFitBounds) {
        return;
      }

      // This is the case where we are drawing a new polygon. First draw centering.
      if (drawingCoordinates.length === 0) {
        const initialCoordinates = [
          {
            lat: (markers[0]?.lat ?? center?.lat ?? DEFAULT_MAP_CENTER.lat) + 0.001,
            lng: markers[0]?.lng ?? center?.lng ?? DEFAULT_MAP_CENTER.lng,
          },
          {
            lat: (markers[0]?.lat ?? center?.lat ?? DEFAULT_MAP_CENTER.lat) - 0.001,
            lng: (markers[0]?.lng ?? center?.lng ?? DEFAULT_MAP_CENTER.lng) + 0.001,
          },
          {
            lat: (markers[0]?.lat ?? center?.lat ?? DEFAULT_MAP_CENTER.lat) - 0.001,
            lng: (markers[0]?.lng ?? center?.lng ?? DEFAULT_MAP_CENTER.lng) - 0.001,
          },
        ];
        const bounds = new window.google.maps.LatLngBounds();
        initialCoordinates.forEach((coordinate) => bounds.extend(coordinate));
        fitBounds(bounds);
        onPolygonEdit?.(initialCoordinates);
      } else {
        // This is the case where we are editing an existing polygon. First draw centering.
        const bounds = new window.google.maps.LatLngBounds();
        drawingCoordinates.forEach((coordinate) => bounds.extend(coordinate));
        const formattedMarkers = markers.map(({ lat, lng }) => ({
          lat,
          lng,
        }));
        formattedMarkers.forEach((marker) => bounds.extend(marker));
        fitBounds(bounds);
      }
    }
  }, [drawingMode, mapInstance, skipDrawingModeFitBounds]);

  // We use this dependency to be less aggressive with the fitBounds calls
  // Updating a marker should not trigger a fitBounds call anymore
  const sortedMarkerIdString = useMemo(
    () =>
      Array.from(new Set(markers.map((marker) => marker.id)))
        .sort()
        .toString(),
    [markers],
  );

  // Reset initial bounds tracking when allowOverrideUserBounds is enabled
  // This allows the "Back to Live" functionality to work properly
  // Also reset when markers change AND allowOverrideUserBounds is true (switching orders)
  useEffect(() => {
    if (allowOverrideUserBounds) {
      initialBoundsSetRef.current = false;
      setUserHasInteracted(false);
    }
  }, [allowOverrideUserBounds, sortedMarkerIdString]);

  // Modify the bounds effect - only auto-fit on initial load, never after user interaction
  // This prevents the map from snapping back on hover or RTU updates
  useEffect(() => {
    if (mapInstance && markers.length > 0) {
      // Skip if user has already interacted with the map AND bounds have been set
      // This prevents the map from snapping back on hover or RTU updates
      if (initialBoundsSetRef.current && userHasInteracted) {
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      // Note: drawingCoordinates are intentionally excluded from bounds calculation
      // to prevent zoom changes when switching geofence types in the Geofence Reports panel
      const formattedMarkers = markers.map(({ lat, lng }) => ({
        lat,
        lng,
      }));
      formattedMarkers.forEach((marker) => bounds.extend(marker));
      markers.forEach((marker) => {
        if (
          (marker.type === MarkerType.site ||
            marker.type === MarkerType.pickup_site ||
            marker.type === MarkerType.dropoff_site ||
            marker.type === MarkerType.staging_site ||
            marker.type === MarkerType.both_site) &&
          marker.geofenceType === GeofenceType.polygon
        ) {
          marker.coordinates.forEach((polygon) => {
            polygon.forEach(([lng, lat]) => bounds.extend({ lat, lng }));
          });
        }
      });
      markers.forEach((marker) => {
        if (
          (marker.type === MarkerType.site ||
            marker.type === MarkerType.pickup_site ||
            marker.type === MarkerType.dropoff_site ||
            marker.type === MarkerType.staging_site ||
            marker.type === MarkerType.both_site) &&
          marker.geofenceType === GeofenceType.circle
        ) {
          const center =
            'circleCenter' in marker && marker.circleCenter
              ? marker.circleCenter
              : { lat: marker.lat, lng: marker.lng };
          const circle = new google.maps.Circle({
            center,
            radius: Math.max(...marker.radii),
          });
          const circleBounds = circle.getBounds();
          if (circleBounds) {
            bounds.extend(circleBounds.getNorthEast());
            bounds.extend(circleBounds.getSouthWest());
          }
        }
      });

      // Only fit bounds if we haven't set initial bounds yet
      if (!initialBoundsSetRef.current) {
        fitBounds(bounds);
        initialBoundsSetRef.current = true;
        // Notify parent that bounds have been reset (so it can clear reset flags)
        onBoundsReset?.();
      }
    }
    // Include allowOverrideUserBounds to ensure bounds are recalculated when view changes
  }, [
    mapInstance,
    sortedMarkerIdString,
    userHasInteracted,
    allowOverrideUserBounds,
    onBoundsReset,
  ]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      // If the map is recreated in place without an unmount, a previous
      // instance's pending cap lift would clear THIS instance's lift through
      // the shared refs and leave the new map stuck at maxZoom.
      clearPendingMaxZoomCapLift();
      setMapInstance(map);
      setUserHasInteracted(false);
      isProgrammaticMapEvent.current = true;

      // Adding custom controls (layers toggle can be hidden e.g. on Job Geo Debug)
      if (!hideLayersToggle) {
        // If the map is recreated in place without an unmount, drop the previous
        // control's root first.
        unmountMapLayersToggleRoot(mapLayersToggleRootRef);
        const mapLayersToggleDiv = document.createElement('div');
        const mapLayersToggleRoot = createRoot(mapLayersToggleDiv);
        mapLayersToggleRootRef.current = mapLayersToggleRoot;
        // This is a fresh React root Google Maps owns, detached from the app tree,
        // so it inherits none of the app's context — including the MUI
        // ThemeProvider. Re-provide the active theme here or any control that reads
        // theme.semantic.* via useTheme() gets MUI's bare default theme (no
        // .semantic) and throws on mount, white-screening the page
        // (Sentry HORIZON-FE-310).
        mapLayersToggleRoot.render(
          <ThemeProvider theme={theme}>
            <MapLayersToggle map={map} sx={{ marginTop: '10px' }} />
          </ThemeProvider>,
        );
        map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(
          mapLayersToggleDiv,
        );
      }
    },
    [
      setMapInstance,
      handleUserInteraction,
      hideLayersToggle,
      theme,
      clearPendingMaxZoomCapLift,
    ],
  );

  const onMapUnmount = useCallback(() => {
    // Google Maps drops the layers control's DOM with the map, but its detached
    // React root stays mounted — the MobX observer subscription on userStore
    // retains the whole tree and its effects keep firing (zombie KML refetch on
    // company change). Unmount it explicitly (TRE-15924).
    unmountMapLayersToggleRoot(mapLayersToggleRootRef);
    setMapInstance(null);
    // Clear any pending recenter timeout
    clearTimeout(recenterTimeoutRef?.current ?? undefined);
    recenterTimeoutRef.current = null;
    clearPendingMaxZoomCapLift();
  }, [setMapInstance, clearPendingMaxZoomCapLift]);

  const onMapTypeIdChanged = useCallback(() => {
    const newMapTypeId = mapInstance?.getMapTypeId() as MapTypeId;
    if (newMapTypeId) {
      setMapTypeId(newMapTypeId);
    }
  }, [mapInstance, setMapTypeId]);

  const onEdit = useCallback(() => {
    if (drawableRef.current) {
      const nextPath = drawableRef.current
        .getPath()
        .getArray()
        .map((latLng) => {
          return { lat: latLng.lat(), lng: latLng.lng() };
        });
      onPolygonEdit?.(nextPath);
    }
  }, [onPolygonEdit]);

  const onPolygonLoad = useCallback((polygon: google.maps.Polygon) => {
    drawableRef.current = polygon;
    // No path listeners (set_at/insert_at/remove_at): they can fire when map tiles load during
    // drag and cause spurious callbacks. We only callback on drag end / mouse up below.
  }, []);

  const cleanupEditablePolygon = useCallback(() => {
    // Please see here for more: https://codesandbox.io/p/sandbox/reactgooglemapsapi-editing-a-polygon-popr2
    // @ts-expect-error - types are wrong. This is found in examples and needed
    listenersRef.current.forEach((lis) => lis?.remove());
    drawableRef.current = null;
  }, []);

  /**
   * Determine if DeckGL layers are being used for rendering.
   * When overlayLayers is provided (Live Map), static sites use DeckGL.
   * When not provided (Order/Site modals), use traditional Google Maps rendering.
   * When WebGL is unavailable the overlay was never created, so sites fall
   * back to traditional rendering too (Sentry HORIZON-FE-2S0).
   */
  const useDeckGLForSites =
    Boolean(overlayLayers && overlayLayers.length > 0) && isWebGLAvailable;

  const circles = useMemo(
    () =>
      buildCircles({
        markers,
        useDeckGLForSites,
        editableCircleCenter,
        editableCircleRadius,
        hasEditableCircleChange: Boolean(onEditableCircleChange),
        editableCircleReplacesPrimaryMarker,
        selectedCircleMarkerId,
      }),
    [
      markers,
      useDeckGLForSites,
      editableCircleCenter,
      editableCircleRadius,
      onEditableCircleChange,
      editableCircleReplacesPrimaryMarker,
      selectedCircleMarkerId,
    ],
  );

  const polygons = useMemo(
    () => buildPolygons({ markers, useDeckGLForSites, selectedCircleMarkerId }),
    [markers, useDeckGLForSites, selectedCircleMarkerId],
  );

  // Recenter the map if the recenter position goes out of view (or always if forceRecenter is true)
  useEffect(() => {
    if (!mapInstance || !isFiniteLngLat(recenterPosition)) return;

    const bounds = mapInstance.getBounds();
    if (!bounds && !forceRecenter) return;

    // Check if the recenter position is within the current map bounds
    const isInBounds = bounds?.contains(recenterPosition) ?? false;

    if (forceRecenter || !isInBounds) {
      // Clear any existing timeout to prevent race conditions
      if (recenterTimeoutRef.current) {
        clearTimeout(recenterTimeoutRef.current);
      }

      // Smoothly pan to the recenter position
      isProgrammaticMapEvent.current = true;
      mapInstance.panTo(recenterPosition);

      // Reset the isProgrammaticChange flag after animation
      recenterTimeoutRef.current = setTimeout(() => {
        isProgrammaticMapEvent.current = false;
        recenterTimeoutRef.current = null;
      }, MAP_RECENTER_SPEED_MS); // Adjust timing based on pan animation duration
    }
  }, [mapInstance, recenterPosition, forceRecenter]);

  // Stable reference — a new center object on each render triggers
  // map.setCenter via useEffect inside @react-google-maps/api, which snaps
  // the viewport back and breaks panning.
  const mapCenter = useMemo(() => {
    if (markers.length > 0 || drawingCoordinates.length > 0) {
      return undefined;
    }

    const candidate = {
      lng: center?.lng ?? DEFAULT_MAP_CENTER.lng,
      lat: center?.lat ?? DEFAULT_MAP_CENTER.lat,
    };

    // A NaN center would reach setCenter and corrupt the projection, crashing
    // the deck.gl overlay (Sentry HORIZON-FE-2XP). Fall back to the default.
    return isFiniteLngLat(candidate) ? candidate : DEFAULT_MAP_CENTER;
  }, [markers.length, drawingCoordinates.length, center?.lng, center?.lat]);

  // Stable reference — a new options object on each render would trigger
  // map.setOptions, which rebuilds Google Maps' native zoom controls and can
  // swap the button DOM node between mousedown and mouseup, suppressing click.
  const isDark = theme.palette.mode === 'dark';
  // Satellite/hybrid imagery must not be recolored; only the vector roadmap view
  // gets the dark treatment.
  const isImageryView = mapTypeId === 'satellite' || mapTypeId === 'hybrid';
  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      gestureHandling: 'greedy',
      mapTypeId,
      zoomControl: true,
      streetViewControl: false,
      rotateControl: true,
      scaleControl: true,
      styles: getMapStyles(isDark, isImageryView),
    }),
    [mapTypeId, isDark, isImageryView],
  );

  return (
    <>
      {isLoaded && (
        <GoogleMap
          mapContainerClassName={onClick ? 'map-pointer-area' : ''}
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={mapCenter}
          zoom={14}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          onBoundsChanged={handleUserInteraction}
          onMapTypeIdChanged={onMapTypeIdChanged}
          onClick={(event) => {
            if (onClick && event.latLng) {
              onClick({
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
              });
            }
          }}
          options={mapOptions}
        >
          {/* Filter markers based on context:
              - When using DeckGL (Live Map): Filter out truck and static site markers
              - When not using DeckGL (modals): Keep site markers for TreadMarker rendering */}
          {markers
            .filter((marker) => {
              // Always filter out trucks when using DeckGL
              if (useDeckGLForSites && marker.type === MarkerType.truck) {
                return false;
              }
              // Filter out static site markers only when using DeckGL
              if (
                useDeckGLForSites &&
                (marker.type === MarkerType.site ||
                  marker.type === MarkerType.pickup_site ||
                  marker.type === MarkerType.dropoff_site ||
                  marker.type === MarkerType.staging_site ||
                  marker.type === MarkerType.both_site)
              ) {
                return false;
              }
              return true;
            })
            .map((marker) => {
              return <TreadMarker key={marker.id} {...marker} />;
            })}
          {/* Circle geofences - includes moving sites (always) and static sites (when not using DeckGL) */}
          {circles.map((circle, index) => (
            <Circle
              key={`circle-${index}`}
              center={circle.center}
              radius={circle.radius}
              options={{
                fillOpacity: circle.isDefaultSite ? 0.1 : 0.25 - index * 0.05,
                fillColor: circle.fillColor,
                strokeOpacity: circle.isDefaultSite ? 0.25 : 0.5,
                strokeWeight: 2,
                strokeColor: circle.fillColor,
              }}
            />
          ))}
          {!isNull(editableCircleCenter) &&
            !isNull(editableCircleRadius) &&
            editableCircleRadius > 0 &&
            onEditableCircleChange && (
              <EditableCircle
                center={editableCircleCenter}
                radius={editableCircleRadius}
                onEdit={onEditableCircleChange}
              />
            )}
          {/* Polygon geofences - only rendered when not using DeckGL (for modals) */}
          {polygons.map((polygon) => (
            <Polygon
              key={`polygon-${polygon.id}`}
              paths={polygon.paths}
              options={{
                fillColor: polygon.fillColor,
                fillOpacity: polygon.isDefaultSite ? 0.1 : 0.25,
                strokeColor: polygon.fillColor,
                strokeOpacity: polygon.isDefaultSite ? 0.3 : 0.8,
                strokeWeight: 2,
              }}
            />
          ))}
          {emptyText && markers.length === 0 && drawingCoordinates.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                height: '100%',
                width: '100%',
                // Scrim over the map for the empty state; deep-black in dark mode so
                // it doesn't wash the map back to light (rgba of semantic.surface).
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(10,24,32,0.8)'
                    : 'rgba(255,255,255,0.8)',
                display: 'grid',
                justifyContent: 'center',
                alignContent: 'center',
              }}
            >
              <Typography variant="h5">{emptyText}</Typography>
            </Box>
          )}
          {drawingMode && (
            <Polygon
              editable
              draggable
              key={`drawing-polygon`}
              paths={drawingCoordinates}
              onLoad={onPolygonLoad}
              onUnmount={cleanupEditablePolygon}
              onMouseUp={onEdit}
              onDragEnd={onEdit}
              onRightClick={(e) => {
                e.domEvent?.preventDefault();
                e.domEvent?.stopPropagation();
              }}
              options={
                {
                  fillColor: 'orange',
                  fillOpacity: 0.35,
                  strokeColor: 'orange',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  suppressUndo: true, // Undocumented Google Maps API: hides "undo last edit" overlay
                } as google.maps.PolygonOptions
              }
            />
          )}
          {readOnlyGeofencePolygon && readOnlyGeofencePolygon.length >= 3 && (
            <Polygon
              key="read-only-geofence-polygon"
              paths={readOnlyGeofencePolygon}
              options={{
                fillColor: '#9e9e9e',
                fillOpacity: 0.2,
                strokeColor: '#757575',
                strokeOpacity: 0.7,
                strokeWeight: 2,
              }}
            />
          )}
          {readOnlyGeofenceCircle && readOnlyGeofenceCircle.radius > 0 && (
            <Circle
              key="read-only-geofence-circle"
              center={readOnlyGeofenceCircle.center}
              radius={readOnlyGeofenceCircle.radius}
              options={{
                fillColor: '#9e9e9e',
                fillOpacity: 0.2,
                strokeColor: '#757575',
                strokeOpacity: 0.7,
                strokeWeight: 2,
              }}
            />
          )}

          {mapInstance &&
            Children.map(children, (child) => {
              if (isValidElement(child)) {
                return cloneElement(child, { map: mapInstance });
              }
              return null;
            })}
        </GoogleMap>
      )}
    </>
  );
}
